import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useSearchParams } from 'react-router-dom';
import { ExternalLink, Github } from 'lucide-react';
import { PostCardSkeleton } from '@/components/ui/skeleton';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import PageTransition from '@/components/PageTransition';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { generateBreadcrumbSchema } from '@/utils/seoHelpers';

interface Project {
  id: string;
  title: string;
  slug: string;
  description: string;
  thumbnail: string;
  category: string[];
  date: string;
}

const PROJECTS_PER_PAGE = 6;

const Projects = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentCategory, setCurrentCategory] = useState(searchParams.get('category') || 'All');
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<string[]>(['All']);

  useScrollReveal([loading, filteredProjects]);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    filterProjects();
  }, [allProjects, currentCategory, searchTerm]);

  useEffect(() => {
    const urlCategory = searchParams.get('category');
    const urlSearch = searchParams.get('search');
    
    if (urlCategory && urlCategory !== currentCategory) {
      setCurrentCategory(urlCategory);
    }
    
    if (urlSearch !== null && urlSearch !== searchTerm) {
      setSearchTerm(urlSearch);
    }
  }, [searchParams]);

  const fetchProjects = async () => {
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      const { data, error } = await supabase
        .from('posts')
        .select('*')
        .eq('published', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const transformedProjects = data?.map(project => ({
        id: project.id,
        title: project.title,
        slug: project.slug,
        description: project.description,
        thumbnail: project.thumbnail,
        category: project.category,
        date: project.created_at,
      })) || [];

      setAllProjects(transformedProjects);
      
      // Extract unique categories
      const uniqueCategories = new Set<string>(['All']);
      transformedProjects.forEach((project: Project) => {
        project.category.forEach(cat => uniqueCategories.add(cat));
      });
      setCategories(Array.from(uniqueCategories));
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setLoading(false);
    }
  };

  const filterProjects = () => {
    let filtered = allProjects.filter(project => {
      const matchesCategory = currentCategory === 'All' || project.category.includes(currentCategory);
      const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          project.description.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
    setFilteredProjects(filtered);
  };

  const handleCategoryChange = (category: string) => {
    setCurrentCategory(category);
    setCurrentPage(1);
    
    if (category === 'All') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', category);
    }
    setSearchParams(searchParams);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalPages = Math.ceil(filteredProjects.length / PROJECTS_PER_PAGE);
  const startIndex = (currentPage - 1) * PROJECTS_PER_PAGE;
  const paginatedProjects = filteredProjects.slice(startIndex, startIndex + PROJECTS_PER_PAGE);

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: "Home", url: "https://invizher.com" },
    { name: "Projects", url: "https://invizher.com/projects" }
  ]);

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "InvizHer Projects",
    "description": "Web development projects, applications, and coding portfolios with live demos and source code",
    "url": "https://invizher.com/projects",
    "publisher": {
      "@type": "Organization",
      "name": "InvizHer",
      "logo": {
        "@type": "ImageObject",
        "url": "https://invizher.com/favicon.ico"
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Projects | InvizHer - Web Development Projects & Portfolio</title>
        <meta name="description" content="Explore innovative web development projects, applications, and coding portfolios. View live demos, source code, and learn from real-world implementations." />
        <meta name="keywords" content="web development projects, coding portfolio, programming projects, React projects, JavaScript applications, open source projects" />
        <link rel="canonical" href="https://invizher.com/projects" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://invizher.com/projects" />
        <meta property="og:title" content="Web Development Projects | InvizHer" />
        <meta property="og:description" content="Explore innovative web development projects with live demos and source code." />
        <meta property="og:site_name" content="InvizHer" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Development Projects | InvizHer" />
        <meta name="twitter:description" content="Explore web development projects and coding portfolios." />
        
        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify([breadcrumbSchema, collectionSchema])}
        </script>
      </Helmet>

      <PageTransition>
        <div className="min-h-screen bg-background">
          {/* Dynamic Background */}
          <div className="fixed inset-0 -z-10 overflow-hidden">
            <div className="absolute top-0 left-0 w-screen h-screen opacity-10">
              <div className="absolute top-0 left-1/4 w-90 h-90 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl"></div>
              <div className="absolute top-1/3 right-1/4 w-90 h-90 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl"></div>
              <div className="absolute bottom-1/4 left-1/3 w-90 h-90 bg-pink-400 rounded-full mix-blend-multiply filter blur-xl"></div>
            </div>
          </div>

          <Header />

          <main className="pt-28 pb-20" role="main">
            <div className="container mx-auto px-4 mb-14 max-w-7xl">
              {/* Page Header */}
              <div className="text-center py-8 scroll-reveal">
                <h1 className="text-3xl font-bold px-4 mb-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Explore Projects
                </h1>
                <p className="text-gray-600 px-4">Browse our development projects with live previews and source code</p>
              </div>

              {/* Search and Filter Section */}
              <div className="mx-auto max-w-4xl mb-8 scroll-reveal">
                <div className="rounded-2xl p-4 md:p-6 mb-6">
                  <div className="flex flex-col md:flex-row gap-4">
                    {/* Search Bar */}
                    <div className="relative flex-1" role="search">
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="search-input w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        placeholder="Search projects..."
                      />
                      {searchTerm && (
                        <button 
                          onClick={() => setSearchTerm('')}
                          className="search-clear absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                        <i className="fas fa-search"></i>
                      </div>
                    </div>
                  </div>
                  
                  {/* Categories */}
                  <div className="mt-4 categories-container">
                    <div className="flex gap-2 min-w-max px-1 py-2">
                      {categories.map(category => (
                        <button
                          key={category}
                          onClick={() => handleCategoryChange(category)}
                          className={`category-pill px-4 py-2 rounded-full glass hover:border-blue-500 transition-all flex items-center ${
                            currentCategory === category ? 'active selected' : ''
                          }`}
                        >
                          <span>{category}</span>
                          {currentCategory === category && category !== 'All' && (
                            <span 
                              className="deselect-btn ml-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCategoryChange('All');
                              }}
                            >
                              <i className="fas fa-times"></i>
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Search Results Message */}
              {(searchTerm || currentCategory !== 'All') && (
                <div className="search-results-message glass rounded-xl mx-2 show scroll-reveal">
                  <div className="container max-w-4xl mx-auto px-6 py-3">
                    <div className="flex items-center justify-between">
                      <p className="text-gray-700">
                        {searchTerm && <>Results for "<span className="search-query">{searchTerm}</span>"</>}
                      </p>
                      <span className="text-sm text-gray-500">
                        {filteredProjects.length} projects found{currentCategory !== 'All' && ` in ${currentCategory}`}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Projects Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto px-2">
                {loading ? (
                  // Skeleton loaders
                  Array(PROJECTS_PER_PAGE).fill(0).map((_, i) => (
                    <PostCardSkeleton key={i} />
                  ))
                ) : paginatedProjects.length > 0 ? (
                  paginatedProjects.map(project => {
                    const displayCategory = currentCategory !== 'All' && project.category.includes(currentCategory)
                      ? currentCategory
                      : project.category[0];

                    return (
                      <article 
                        key={project.id} 
                        className="scroll-reveal group" 
                        role="article"
                      >
                        <Link to={`/posts/${project.slug}`} className="block">
                          <div className="glass-morphism rounded-2xl overflow-hidden hover:shadow-xl transition-all p-6 md:p-8 border border-border/50">
                            {/* Top Section: Thumbnail + Category Badge */}
                            <div className="flex items-start justify-between mb-6">
                              {/* Small Square Thumbnail */}
                              <div className="w-24 h-16 rounded-2xl overflow-hidden flex-shrink-0 border border-border">
                                <img 
                                  src={project.thumbnail} 
                                  srcSet={`${project.thumbnail}?width=200&quality=75 200w, ${project.thumbnail}?width=300&quality=75 300w`}
                                  sizes="96px"
                                  alt={project.title} 
                                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                                  loading="lazy"
                                  width="96"
                                  height="64"
                                />
                              </div>
                              
                              {/* Category Badge */}
                              <span className="px-4 py-1 bg-gradient-to-r from-blue-500/10 to-purple-500/10 text-blue-600 rounded-full text-sm font-medium">
                                {displayCategory}
                              </span>
                            </div>

                            {/* Title */}
                            <h3 className="text-xl md:text-2xl font-bold mb-3 line-clamp-2">
                              {project.title}
                            </h3>
                            
                            {/* Description */}
                            <p className="text-muted-foreground mb-6 line-clamp-2">
                              {project.description}
                            </p>
                            
                            {/* Code Preview Button */}
                            <Button 
                              variant="default"
                              className="w-full rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:opacity-90 transition-opacity"
                            >
                              Code and Preview
                            </Button>
                          </div>
                        </Link>
                      </article>
                    );
                  })
                ) : (
                  <div className="col-span-full text-center py-12">
                    <div className="text-muted-foreground">
                      <i className="fas fa-search text-4xl mb-4 opacity-50"></i>
                      <p className="text-xl">No projects found matching your criteria</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-12 flex justify-center scroll-reveal">
                  <div className="flex gap-2">
                    <button
                      className={`px-4 py-2 rounded-lg glass ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:border-blue-500'}`}
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                      if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                        return (
                          <button
                            key={page}
                            className={`px-4 py-2 rounded-lg border ${page === currentPage ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white' : 'border-gray-200 hover:border-blue-500'}`}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={page} className="px-4 py-2">...</span>;
                      }
                      return null;
                    })}
                    
                    <button
                      className={`px-4 py-2 rounded-lg glass ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:border-blue-500'}`}
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </main>

          <Footer />
        </div>
      </PageTransition>
    </>
  );
};

export default Projects;
